/**
 * Data Hub — Normalized tab.
 *
 * Metric × date heatmap with the reference app's toggles: By date ↔ By account
 * and Normalized 0–100 ↔ Actual + UOM, plus the period selector.
 */
import { useState } from "react";

import DropdownSelector from "../../Components/DropdownSelector";
import { MetricMatrix, type MatrixValueMode } from "../components/MetricMatrix";
import { TabCard, TabError, TabLoading } from "../components/TabState";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

export function NormalizedTab() {
  const [dataset] = useActiveDataset();
  const [axis, setAxis] = useState<"date" | "account">("date");
  const [mode, setMode] = useState<MatrixValueMode>("normalized");
  const [period, setPeriod] = useState("12m");
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getNormalizedMatrix({ axis }),
    [dataset, axis]
  );

  if (loading && !data)
    return <TabLoading label="Loading normalized values…" />;
  if (error) return <TabError error={error} />;
  if (!data) return null;

  const rowNoun = axis === "account" ? "accounts" : "dates";

  return (
    <TabCard
      title="Normalized values matrix"
      subtitle={`${data.metrics.length} metrics · ${data.rows.length} ${rowNoun}`}
      actions={
        <>
          <DropdownSelector
            label="View"
            selected={axis}
            options={[
              { value: "date", label: "By date" },
              { value: "account", label: "By account" },
            ]}
            onSelect={v => setAxis(v as "date" | "account")}
          />
          <DropdownSelector
            label="Period"
            selected={period}
            options={[
              { value: "3m", label: "Last 3 months" },
              { value: "6m", label: "Last 6 months" },
              { value: "12m", label: "Last 12 months" },
            ]}
            onSelect={setPeriod}
          />
          <DropdownSelector
            label="Values"
            selected={mode}
            options={[
              { value: "normalized", label: "Normalized 0–100" },
              { value: "actual", label: "Actual + UOM" },
            ]}
            onSelect={v => setMode(v as MatrixValueMode)}
          />
        </>
      }
    >
      <MetricMatrix
        matrix={data}
        mode={mode}
        rowHeaderLabel={axis === "account" ? "Account" : "Date"}
      />
    </TabCard>
  );
}

export default NormalizedTab;
